import {
  ECardInsertedImageVersion,
  ECardLogoVersion,
  ECardSecondaryImageVersion,
  ECardTextVersion,
  ELanguageType,
  ETextAlignment,
  ETextAnchor,
  ETextFieldType,
} from '@hts/shared/enums';
import {
  ICardAttributeInput,
  ICardAudioItemInput,
  ICardFolderInput,
  IGetCardDataResponse,
  IGetEditOrderDataResponse,
  IImageDimensionsInput,
  IInsertedImageInput,
  ILogoInput,
  ILogoOutput,
  IOrderTextFieldsDataOutput,
  IRotation,
  ISecondaryImageInput,
  ISecondaryImageOutput,
  ITextFieldItemOutput,
  RotationRectType,
  SetReactState,
} from '@hts/shared/types';
import {
  addPercentage,
  calculatePercentageChange,
  decryptData,
  degToRad,
  generateHashByArray,
  getCenteredOffset,
  getFontSizeNumber,
  loadImageFromUrl,
} from '@hts/shared/utils';
import Konva from 'konva';
import { IRect } from 'konva/lib/types';
import { v4 as uuid } from 'uuid';
import { INCLINE_MULTIPLIER, LETTER_SPACE_MULTIPLIER } from '../consts';
import { EMusicItemType } from '../enums';
import {
  IFolderMusicItemState,
  ILogoImageState,
  IMusicAudioItemState,
  IMusicItemState,
  INewCardState,
  INewFieldItem,
  IPreviewUrlState,
  ISecondaryImageState,
} from '../types';

export const getDecryptEditOrderData = async (
  editOrderData: string
): Promise<IGetEditOrderDataResponse | null> => {
  const decryptedEditOrderData = await decryptData(editOrderData);

  return decryptedEditOrderData as IGetEditOrderDataResponse | null;
};

export const getDecryptCardData = async (
  cardData: string
): Promise<IGetCardDataResponse | null> => {
  const decryptedCard = await decryptData(cardData);

  return decryptedCard as IGetCardDataResponse | null;
};

interface IGetTextXCoordinatesFromSVGProps {
  containerWidth: number;
  containerHeight: number;
  textAnchor: ETextAnchor;
  containerX: number;
  containerY: number;
}

export const getTextXCoordinatesFromSVG = ({
  containerHeight,
  containerWidth,
  containerX,
  containerY,
  textAnchor,
}: IGetTextXCoordinatesFromSVGProps) => {
  const newY = containerY - containerHeight / 1.3;

  let newX = containerX;

  switch (textAnchor) {
    case ETextAnchor.START:
      newX = containerX;
      break;
    case ETextAnchor.END:
      newX = containerX - containerWidth;
      break;
    case ETextAnchor.MIDDLE:
      newX = containerX - containerWidth / 2;
      break;
  }

  return { newX: Math.floor(newX), newY: Math.floor(newY) };
};

export const getMappedSaveDraftTextFields = (
  fields: INewFieldItem[]
): IOrderTextFieldsDataOutput => {
  const textFields: IOrderTextFieldsDataOutput = {
    text: [],
    fontSize: [],
  };

  fields.forEach((field) => {
    textFields.text.push(field.text || null);
    textFields.fontSize.push(field.changedFontSize ?? getFontSizeNumber(field.font_size));
  });

  return textFields;
};

export const getMappedOrderTextFields = (fields: INewFieldItem[]): ITextFieldItemOutput => {
  const textFields: ITextFieldItemOutput = {
    type: [],
    text: [],
    fontId: [],
    fontSize: [],
    fontColor: [],
    textX: [],
    textY: [],
    blockX: [],
    blockY: [],
    blockRotation: [],
    blockWidth: [],
    blockHeight: [],
    textAlignment: [],
    language: [],
    letterSpacing: [],
    skew: [],
    isHidden: [],
    group: [],
  };

  fields.reduce((acc, field) => {
    const letterSpacing = field.letter_spacing || 0;
    const incline = field.skew || 0;

    let fontSize = '';

    if (typeof field.changedFontSize === 'number') {
      fontSize = `${field.changedFontSize}px`;
    } else {
      fontSize = field.font_size;
    }

    acc.type.push(field.type || ETextFieldType.TEXT);
    acc.text.push(field.text || null);
    acc.fontId.push(field.font?.id || 0);
    acc.fontSize.push(fontSize);
    acc.fontColor.push(field.font_color);
    acc.blockX.push(field.text_position_x);
    acc.blockY.push(field.text_position_y);
    acc.blockWidth.push(field.blockWidth || 0);
    acc.blockHeight.push(field.blockHeight || 0);
    acc.textX.push(field.text_position_x || 0);
    acc.textY.push(field.text_y || 0);
    acc.blockRotation.push(field.text_rotation);
    acc.textAlignment.push(field.textAlignment || ETextAlignment.CENTER);
    acc.language.push(field.text_lang);
    acc.letterSpacing.push(Math.round(letterSpacing * LETTER_SPACE_MULTIPLIER));
    acc.skew.push(Math.round(incline * INCLINE_MULTIPLIER));
    acc.isHidden.push(field.isHidden);
    acc.group.push(field.group);

    return acc;
  }, textFields);

  return textFields;
};

const getClearedTextFields = (backendFields: INewFieldItem[], storageFields: INewFieldItem[]) => {
  const getClearedFields = (fields: INewFieldItem[]) =>
    fields.map(({ id, text, text_lang, changedFontSize, ...clearedFields }) => ({
      ...clearedFields,
    }));

  const clearedBackendFields = getClearedFields(backendFields);
  const clearedStorageFields = getClearedFields(storageFields);

  return { clearedBackendFields, clearedStorageFields };
};

export const getClearedTextFieldsHashes = (
  backendFields: INewFieldItem[],
  storageFields: INewFieldItem[]
) => {
  const { clearedBackendFields, clearedStorageFields } = getClearedTextFields(
    backendFields,
    storageFields
  );

  const storageFieldsHash = generateHashByArray(clearedStorageFields);
  const backendFieldsHash = generateHashByArray(clearedBackendFields);

  return { storageFieldsHash, backendFieldsHash };
};

interface IGetNewPercentageSizesProps {
  widthPercentage: number;
  hightPercentage: number;
  prevWidth: number;
  prevHeight: number;
}

interface IGetCalculateCropSizeProps {
  prevRectWidth: number;
  prevRectHeight: number;
  currentRectWidth: number;
  currentRectHeight: number;
  currentCropX: number;
  currentCropY: number;
  prevCropWidth: number;
  prevCropHeight: number;
}

export const getCalculateCropSize = ({
  prevRectWidth,
  prevRectHeight,
  currentRectWidth,
  currentRectHeight,
  currentCropX,
  currentCropY,
  prevCropWidth,
  prevCropHeight,
}: IGetCalculateCropSizeProps) => {
  const scaleX = currentRectWidth / prevRectWidth;
  const scaleY = currentRectHeight / prevRectHeight;

  const newWidth = prevCropWidth * scaleX;
  const newHeight = prevCropHeight * scaleY;

  const prevCropOffset = getCenteredOffset(prevCropWidth, prevCropHeight);
  const newCropOffset = getCenteredOffset(newWidth, newHeight);

  const prevCropCenterX = currentCropX + prevCropOffset.x;
  const prevCropCenterY = currentCropY + prevCropOffset.y;

  const newCropX = prevCropCenterX - newCropOffset.x;
  const newCropY = prevCropCenterY - newCropOffset.y;

  return {
    x: newCropX,
    y: newCropY,
    width: newWidth,
    height: newHeight,
  };
};

export const getNewPercentageSizes = ({
  widthPercentage,
  hightPercentage,
  prevWidth,
  prevHeight,
}: IGetNewPercentageSizesProps) => {
  const newWidth =
    widthPercentage === 0 ? prevWidth : prevWidth + (prevWidth * widthPercentage) / 100;

  const newHeight =
    hightPercentage === 0 ? prevHeight : prevHeight + (prevHeight * hightPercentage) / 100;

  return { width: Math.ceil(newWidth), height: Math.ceil(newHeight) };
};

interface IGetLogoCropSizesIfLogoNotSetResult {
  cropWidth: number;
  cropHeight: number;
  cropX: number;
  cropY: number;
}

const getLogoCropSizesIfLogoNotSet = (
  image?: HTMLImageElement
): IGetLogoCropSizesIfLogoNotSetResult => {
  const result = {
    cropHeight: 0,
    cropWidth: 0,
    cropX: 0,
    cropY: 0,
  };

  if (!image) return result;

  const imageSize = Math.max(image.width, image.height);
  const reducedCoefficient = 1.5;

  const cropHeight = imageSize / reducedCoefficient;
  const cropWidth = imageSize / reducedCoefficient;
  const cropX = image.width / 2 - imageSize / reducedCoefficient / 2;
  const cropY = image.height / 2 - imageSize / reducedCoefficient / 2;

  result.cropHeight = Math.round(cropHeight);
  result.cropWidth = Math.round(cropWidth);
  result.cropX = Math.round(cropX);
  result.cropY = Math.round(cropY);

  return result;
};

interface IGetCorrectLogoCropSizesProps {
  image?: HTMLImageElement;
  primaryImage?: HTMLImageElement;
  currentCropWidth: number;
  currentCropHeight: number;
  rectWidth: number;
  rectHeight: number;
}

export const getCorrectLogoCropSizes = ({
  image,
  primaryImage,
  currentCropHeight,
  currentCropWidth,
  rectHeight,
  rectWidth,
}: IGetCorrectLogoCropSizesProps) => {
  const imgWidth = image?.width || 0;
  const imgHeight = image?.height || 0;

  let cropWidth = currentCropWidth || imgWidth;
  let cropHeight = currentCropHeight || imgHeight;

  if ((rectWidth === 0 || rectHeight === 0) && primaryImage) {
    const primaryImageSize = Math.max(primaryImage.width, primaryImage.height);
    const reducedCoefficient = 1.5;

    cropWidth = primaryImageSize / reducedCoefficient;
    cropHeight = primaryImageSize / reducedCoefficient;
  }

  // Zero means that the image is default and crop size should be set to the default image size
  if (currentCropHeight === 0 || currentCropWidth === 0) {
    cropWidth = imgWidth;
    cropHeight = imgHeight;
  }

  return {
    cropWidth: Math.round(cropWidth),
    cropHeight: Math.round(cropHeight),
  };
};

interface IGetCorrectLogoCropSizes {
  image?: HTMLImageElement;
  primaryImage?: HTMLImageElement;
  currentCropWidth: number;
  currentCropHeight: number;
  currentCropX: number;
  currentCropY: number;
  rectWidth: number;
  rectHeight: number;
  isEnabledButNotSet: boolean;
}

export const getCorrectLogoCropData = (
  data: IGetCorrectLogoCropSizes
): IGetLogoCropSizesIfLogoNotSetResult => {
  if (data.isEnabledButNotSet) {
    return getLogoCropSizesIfLogoNotSet(data.image);
  }

  const { cropWidth, cropHeight } = getCorrectLogoCropSizes(data);

  return {
    cropWidth,
    cropHeight,
    cropX: data.currentCropX,
    cropY: data.currentCropY,
  };
};

export const getTextFieldsState = (
  cardAttributes: ICardAttributeInput[],
  version: ECardTextVersion
): INewFieldItem[] =>
  cardAttributes.map((item) => {
    const correctText = item.text || '';

    const result: INewFieldItem = {
      type: item.type || ETextFieldType.TEXT,
      text: correctText,
      blockHeight: item.blockHeight || 0,
      blockWidth: item.blockWidth || 0,
      font_size: item.font_size || '0px',
      font_color: item.font_color || '#000000',
      text_position_x: item.text_position_x || 0,
      text_position_y: item.text_position_y || 0,
      text_rotation: item.text_rotation || 0,
      isHidden: item.isHidden || false,
      text_lang: item.text_lang || ELanguageType.HEBREW,
      text_x: item.text_x || 0,
      text_y: item.text_y || 0,
      text_alignment: item.text_alignment || null,
      textAlignment: item.textAlignment || ETextAlignment.CENTER,
      font: item.font || null,
      group: item.group || null,
      font_id: item.font?.id || undefined,

      id: uuid(),
      defaultText: correctText,
      initial_text_lang: item.text_lang || ELanguageType.HEBREW,
      changedFontSize: getFontSizeNumber(item.font_size || '0px'),
      letter_spacing: item.letter_spacing ? item.letter_spacing / LETTER_SPACE_MULTIPLIER : 0,
      skew: item.skew ? item.skew / INCLINE_MULTIPLIER : 0,
      recalculatedByTextAnchor: false,
    };

    if (version === ECardTextVersion.V_2) {
      const { x, y } = getCorrectedCoordinatesByCenterOffset({
        width: item.blockWidth || 0,
        height: item.blockHeight || 0,
        x: item.text_position_x || 0,
        y: item.text_position_y || 0,
        rotation: item.text_rotation || 0,
      });

      result.text_position_x = x;
      result.text_position_y = y;
    }

    return result;
  });

export const getSecondaryImageState = (
  secondaryImage?: ISecondaryImageInput | null
): ISecondaryImageState | null => {
  if (!secondaryImage) return null;

  const { crop_x, crop_y, crop_width, crop_height, image } = secondaryImage;

  const result: ISecondaryImageState = {
    ...secondaryImage,
    currentImageUrl: image.fileUrl,
    currentCropHeight: crop_height,
    currentCropWidth: crop_width,
    currentCropX: crop_x,
    currentCropY: crop_y,
    isDefaultImage: true,
    clientFileImage: null,
  };

  if (secondaryImage.version === ECardSecondaryImageVersion.V_1) {
    const { x, y } = getCorrectedCoordinatesByCenterOffset({
      width: result.width,
      height: result.height,
      x: result.x,
      y: result.y,
      rotation: result.rotation,
    });

    result.x = x;
    result.y = y;
  }

  return result;
};

export const logoIsEnabledButNotSet = ({
  width,
  height,
  x,
  y,
  rotation,
  is_enabled,
}: ILogoInput) => {
  return Boolean(
    is_enabled &&
      (y === null || x === null || width === null || height === null || rotation === null)
  );
};

export const getTextFieldsForRequest = (fields: INewFieldItem[]) =>
  fields.map((field) => ({
    text: field.text || null,
    fontSize: field.changedFontSize ?? getFontSizeNumber(field.font_size),
  }));

export const getLogoState = (logo?: ILogoInput | null): ILogoImageState | null => {
  if (!logo) return null;

  const {
    x,
    y,
    width,
    height,
    rotation,
    crop_x,
    crop_y,
    crop_width,
    crop_height,
    image,
    is_enabled,
    is_locked,
  } = logo;

  const result: ILogoImageState = {
    ...logo,
    currentImageUrl: image?.fileUrl || '',
    isEnabledButNotSet: logoIsEnabledButNotSet(logo),
    currentCropHeight: crop_height || 0,
    currentCropWidth: crop_width || 0,
    currentCropX: crop_x || 0,
    currentCropY: crop_y || 0,
    isDefaultImage: true,
    clientFileImage: null,
  };

  if (!is_enabled) {
    return result;
  }

  if (
    y === null ||
    x === null ||
    width === null ||
    height === null ||
    rotation === null ||
    !image
  ) {
    return result;
  }

  if (logo.version === ECardLogoVersion.V_1) {
    const { x: correctX, y: correctY } = getCorrectedCoordinatesByCenterOffset({
      width,
      height,
      x,
      y,
      rotation,
    });

    result.x = correctX;
    result.y = correctY;
  }

  if (width === crop_width && height === crop_height && crop_x === 0 && crop_y === 0) {
    result.currentCropWidth = image.fileDimensions.width;
    result.currentCropHeight = image.fileDimensions.height;
  }

  return result;
};

export const getInsertedImages = (
  insertedImages?: IInsertedImageInput[] | null
): IInsertedImageInput[] => {
  if (!insertedImages) return [];

  return insertedImages.map((image) => {
    const result: IInsertedImageInput = { ...image };

    if (image.version === ECardInsertedImageVersion.V_1) {
      const { x, y } = getCorrectedCoordinatesByCenterOffset({
        width: result.width,
        height: result.height,
        x: result.x,
        y: result.y,
        rotation: result.rotation,
      });

      result.x = x;
      result.y = y;
    }

    return result;
  });
};

export const getSecondaryImageForRequest = (
  secondaryImage: ISecondaryImageState
): ISecondaryImageOutput => ({
  width: secondaryImage.width,
  height: secondaryImage.height,
  x: secondaryImage.x,
  y: secondaryImage.y,
  rotation: secondaryImage.rotation,
  cropX: secondaryImage.currentCropX,
  cropY: secondaryImage.currentCropY,
  cropWidth: secondaryImage.currentCropWidth,
  cropHeight: secondaryImage.currentCropHeight,
  mediaId: secondaryImage.media_id,
  isEnabled: Boolean(secondaryImage.is_enabled),
  isOnTop: Boolean(secondaryImage.is_on_top),
});

export const getLogoForRequest = (logo: ILogoImageState): ILogoOutput => ({
  isLocked: Boolean(logo.is_locked),
  height: logo.height,
  width: logo.width,
  x: logo.x,
  y: logo.y,
  rotation: logo.rotation,
  mediaId: logo.media_id,
  isEnabled: Boolean(logo.is_enabled),
  cropWidth: logo.currentCropWidth,
  cropHeight: logo.currentCropHeight,
  cropX: logo.currentCropX,
  cropY: logo.currentCropY,
});

export const getMainImageSizes = (
  imageDimensions: IImageDimensionsInput | null | undefined,
  image: HTMLImageElement | undefined
) => {
  const mainImageWidth = imageDimensions?.width || 0;
  const mainImageHeight = imageDimensions?.height || 0;

  const width = mainImageWidth || image?.width || 0;
  const height = mainImageHeight || image?.height || 0;

  return { width, height };
};

export const turnRotation = (rotation: number) => {
  let result = rotation;

  if (rotation > -5 && rotation < 5) result = 0;
  else if (rotation > 85 && rotation < 95) result = 90;
  else if (rotation < -85 && rotation > -95) result = -90;
  else if (rotation > 175 && rotation < 185) result = 180;
  else if (rotation < -175 && rotation > -185) result = -180;

  return ((result % 360) + 360) % 360;
};

export const transformDelta = (
  deltaX: number,
  deltaY: number,
  rotation: number
): { x: number; y: number } => {
  const angleRad = degToRad(-rotation);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  const transformedX = deltaX * cos - deltaY * sin;
  const transformedY = deltaX * sin + deltaY * cos;

  return { x: transformedX, y: transformedY };
};

export const getCorrectedCoordinatesByCenterOffset = ({
  width,
  height,
  x,
  y,
  rotation,
}: RotationRectType) => {
  const angleRad = degToRad(rotation);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  const offsetX = (width / 2) * cos - (height / 2) * sin;
  const offsetY = (width / 2) * sin + (height / 2) * cos;

  const newX = x - offsetX;
  const newY = y - offsetY;

  return { x: Math.round(newX), y: Math.round(newY) };
};

interface IGetChangedSizesBySidesProps {
  newCrop: IRect;
  prevCrop: IRect;
}

export const getChangedSizesBySides = ({ newCrop, prevCrop }: IGetChangedSizesBySidesProps) => {
  const anotherCropWidth = newCrop.width !== prevCrop.width;
  const anotherCropHeight = newCrop.height !== prevCrop.height;
  const anotherCropX = newCrop.x !== prevCrop.x;
  const anotherCropY = newCrop.y !== prevCrop.y;

  const changedWidthByLeft = anotherCropX && anotherCropWidth;
  const changedHeightByTop = anotherCropY && anotherCropHeight;

  return {
    changedWidthByLeft,
    changedHeightByTop,
  };
};

interface IGetNewTransformerCoordinatesWithRotationProps {
  changedWidthByLeft: boolean;
  changedHeightByTop: boolean;
  prevRotation: number;
  deltaWidth: number;
  deltaHeight: number;
  prevX: number;
  prevY: number;
}

export const getNewTransformerCoordinatesWithRotation = ({
  changedHeightByTop,
  changedWidthByLeft,
  prevRotation,
  deltaWidth,
  deltaHeight,
  prevX,
  prevY,
}: IGetNewTransformerCoordinatesWithRotationProps) => {
  const angleRad = degToRad(prevRotation);

  let newX = prevX;
  let newY = prevY;

  const setCoordinatesByDelta = (deltaX: number, deltaY: number) => {
    newX = prevX + deltaX;
    newY = prevY + deltaY;
  };

  if (changedWidthByLeft && changedHeightByTop) {
    const deltaX = -deltaWidth * Math.cos(angleRad) + deltaHeight * Math.sin(angleRad);
    const deltaY = -deltaWidth * Math.sin(angleRad) - deltaHeight * Math.cos(angleRad);

    setCoordinatesByDelta(deltaX, deltaY);
  } else if (changedWidthByLeft) {
    const deltaX = -deltaWidth * Math.cos(angleRad);
    const deltaY = -deltaWidth * Math.sin(angleRad);

    setCoordinatesByDelta(deltaX, deltaY);
  } else if (changedHeightByTop) {
    const deltaX = deltaHeight * Math.sin(angleRad);
    const deltaY = -deltaHeight * Math.cos(angleRad);

    setCoordinatesByDelta(deltaX, deltaY);
  }

  return { x: newX, y: newY };
};

interface IGetNewTransformerDataByChangingCropProps {
  prevCrop: IRect;
  prevTransformer: IRect & IRotation;
  newCrop: IRect;
}

export const getNewTransformerDataByChangingCrop = ({
  prevCrop,
  prevTransformer,
  newCrop,
}: IGetNewTransformerDataByChangingCropProps) => {
  const prevWidth = prevTransformer.width;
  const prevHeight = prevTransformer.height;
  const prevX = prevTransformer.x;
  const prevY = prevTransformer.y;
  const prevRotation = prevTransformer.rotation;

  const newWidthPercentage = calculatePercentageChange(prevCrop.width, newCrop.width);
  const newHeightPercentage = calculatePercentageChange(prevCrop.height, newCrop.height);
  const newWidth = addPercentage(prevWidth, newWidthPercentage);
  const newHeight = addPercentage(prevHeight, newHeightPercentage);

  const deltaWidth = newWidth - prevWidth;
  const deltaHeight = newHeight - prevHeight;

  const { changedHeightByTop, changedWidthByLeft } = getChangedSizesBySides({ newCrop, prevCrop });

  let newX = prevX;
  let newY = prevY;

  if (prevRotation === 0) {
    if (changedWidthByLeft) {
      newX = prevX - deltaWidth;
    }

    if (changedHeightByTop) {
      newY = prevY - deltaHeight;
    }
  } else {
    const { x, y } = getNewTransformerCoordinatesWithRotation({
      changedWidthByLeft,
      changedHeightByTop,
      prevRotation,
      deltaWidth,
      deltaHeight,
      prevX,
      prevY,
    });

    newX = x;
    newY = y;
  }

  return {
    x: Math.round(newX),
    y: Math.round(newY),
    width: Math.round(newWidth),
    height: Math.round(newHeight),
  };
};

export const getMusicListState = (
  cardAudios?: ICardAudioItemInput[],
  cardFolders?: ICardFolderInput[]
): IMusicItemState[] => {
  const result: IMusicItemState[] = [];

  if (cardAudios?.length) {
    const audioItems: IMusicAudioItemState[] = cardAudios.map((audio) => ({
      ...audio,
      type: EMusicItemType.audio,
    }));

    result.push(...audioItems);
  }

  if (cardFolders?.length) {
    cardFolders.forEach((folder) => {
      const { media, ...folderWithoutMedia } = folder;

      const folderItem: IFolderMusicItemState = {
        ...folderWithoutMedia,
        type: EMusicItemType.folder,
      };

      result.push(folderItem);

      if (folder.media?.length) {
        const folderAudioItems: IMusicAudioItemState[] = folder.media.map((audio) => ({
          ...audio,
          type: EMusicItemType.audio,
        }));

        result.push(...folderAudioItems);
      }
    });
  }

  return result;
};

interface IGetSecondaryImageDataForRequestProps {
  secondaryImage: ISecondaryImageState;
  formData: FormData;
  getStageBlobForCroppedSecondaryImage: () => Promise<Blob | null>;
}

export const setSecondaryImageFormDataForRequest = async ({
  secondaryImage,
  formData,
  getStageBlobForCroppedSecondaryImage,
}: IGetSecondaryImageDataForRequestProps) => {
  const { isDefaultImage, currentImageUrl, clientFileImage } = secondaryImage;

  let isCorrect = true;

  if (!isDefaultImage && currentImageUrl && clientFileImage) {
    formData.append('newSecondaryImageFile', clientFileImage);
  }

  if (secondaryImage.is_on_top === 0 && currentImageUrl) {
    const croppedSecondaryImageBlob = await getStageBlobForCroppedSecondaryImage();

    if (!croppedSecondaryImageBlob) {
      console.error('SetOrder: croppedSecondaryImageBlob is not defined');

      isCorrect = false;
    } else {
      formData.append('croppedSecondaryImageFile', croppedSecondaryImageBlob);
    }
  }

  return isCorrect;
};

interface ISetLogoFormDataForRequestProps {
  logo: ILogoImageState;
  formData: FormData;
}

export const setLogoFormDataForRequest = async ({
  logo,
  formData,
}: ISetLogoFormDataForRequestProps) => {
  const { isDefaultImage, currentImageUrl, clientFileImage } = logo;

  if (!isDefaultImage && currentImageUrl && clientFileImage) {
    formData.append('newLogoFile', clientFileImage);
  }
};

type IDefaultImageData = Pick<IPreviewUrlState, 'image' | 'imageStatus'>;

export const setPreviewUrlState = async (
  data: Omit<IPreviewUrlState, 'imageStatus' | 'image'>,
  setCard: SetReactState<INewCardState | null>
) => {
  if (!data.url) {
    const nullImage: IDefaultImageData = { image: null, imageStatus: 'failed' };

    setCard((prev) => prev && { ...prev, previewUrl: { ...data, ...nullImage } });

    return;
  }

  const loadingImage: IDefaultImageData = { image: null, imageStatus: 'loading' };

  setCard((prev) => prev && { ...prev, previewUrl: { ...data, ...loadingImage } });

  const loadedImage = await loadImageFromUrl(data.url);

  const finalImage: IDefaultImageData = {
    image: loadedImage,
    imageStatus: loadedImage ? 'loaded' : 'failed',
  };

  setCard((prev) => prev && { ...prev, previewUrl: { ...data, ...finalImage } });
};

interface CalculateFontSizeFitParams {
  text: string;
  containerWidth: number;
  containerHeight: number;
  originalFontSize: number;
  changedFontSize?: number;
  fontFamily?: string;
  direction: 'rtl' | 'ltr';
}

export function calculateFontSizeFit({
  text,
  containerWidth,
  containerHeight,
  originalFontSize,
  changedFontSize = originalFontSize,
  fontFamily,
  direction,
}: CalculateFontSizeFitParams): number | null {
  if (!containerWidth || !containerHeight) return null;

  let changedTextNode: Konva.Text | null = null;

  try {
    changedTextNode = new Konva.Text({
      text,
      fontSize: changedFontSize,
      fontFamily,
      wrap: 'none',
      direction,
    });

    const currentWidth = changedTextNode.width();

    if (currentWidth > containerWidth) {
      const scaleRatio = containerWidth / currentWidth;
      const newFontSize = Math.max(Math.floor(changedFontSize * scaleRatio), 1);

      return newFontSize;
    }

    if (changedFontSize < originalFontSize) {
      const scaleRatio = containerWidth / currentWidth;
      const newFontSize = Math.min(Math.floor(changedFontSize * scaleRatio), originalFontSize);

      return newFontSize;
    }

    return changedFontSize;
  } catch (error) {
    console.error('calculateFontSizeFit error:', error);
    return changedFontSize;
  } finally {
    changedTextNode?.destroy();
  }
}
